import { useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Callout,
  Card,
  Field,
  Input,
  Textarea,
} from "@/components/ui";
import { Select } from "@/components/pick";
import { MAX_RESUME_BYTES, ResumeRow } from "@/components/resume";
import { useSaveProfile, useUploadResume, type LinkKind } from "@/lib/profile";
import { useMe } from "@/lib/session";

export const Route = createFileRoute("/_auth/onboarding")({
  component: Onboarding,
});

const LINK_KINDS: { value: LinkKind; label: string; placeholder: string }[] = [
  { value: "github", label: "GitHub", placeholder: "github.com/you" },
  { value: "linkedin", label: "LinkedIn", placeholder: "linkedin.com/in/you" },
  { value: "portfolio", label: "Portfolio", placeholder: "you.dev" },
  { value: "other", label: "Other", placeholder: "arxiv.org/abs/…" },
];

const placeholderFor = (kind: LinkKind) =>
  LINK_KINDS.find((k) => k.value === kind)!.placeholder;

/** Blank rows the form starts with — the two nearly everyone has. */
const STARTER_LINKS: { kind: LinkKind; url: string }[] = [
  { kind: "github", url: "" },
  { kind: "linkedin", url: "" },
];

function Onboarding() {
  const me = useMe();
  const navigate = useNavigate();
  const profile = me.profile;
  const firstTime = !me.profileComplete;

  const [phone, setPhone] = useState(profile?.phone ?? "");
  const [branch, setBranch] = useState(profile?.branch ?? "");
  const [about, setAbout] = useState(profile?.about ?? "");
  const [links, setLinks] = useState<{ kind: LinkKind; url: string }[]>(
    profile?.links.length
      ? profile.links.map((l) => ({ kind: l.kind as LinkKind, url: l.url }))
      : STARTER_LINKS,
  );

  const save = useSaveProfile();
  const upload = useUploadResume();
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const ABOUT_MAX = 1200;
  const overLong = about.length > ABOUT_MAX;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (overLong) return;
    // The server decides whether that finished onboarding, and says so by
    // stamping completedAt — the portal never decides it for itself, because a
    // client that does can be told otherwise with one fetch. Phone, branch and
    // about are the whole of it; the resume is optional and uploads on its own
    // as soon as it is picked, so it is not part of this submit either way.
    const saved = await save.mutateAsync({ phone, branch, about, links });
    if (saved.completedAt) await navigate({ to: "/dashboard" });
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileError(null);

    // Checked here so a 6MB file is refused instantly instead of after a
    // minute of uploading. The server checks again — and checks the bytes,
    // which is the check that counts.
    if (file.size > MAX_RESUME_BYTES) {
      setFileError("That is larger than 5MB. Export it smaller and try again.");
      return;
    }
    try {
      await upload.mutateAsync(file);
    } catch (error) {
      setFileError((error as Error).message);
    }
  }

  return (
    <div className="flex max-w-[720px] flex-col gap-10">
      <header className="flex flex-col gap-3">
        <p className="mono text-ink-3">
          {firstTime ? "Step one of one" : "Your profile"}
        </p>
        <h1 className="display text-[clamp(30px,5vw,46px)]">
          {firstTime ? `Hello, ${me.user.name.split(" ")[0]}.` : "Your profile"}
        </h1>
        <p className="max-w-[54ch] text-[16px] leading-relaxed text-ink-2">
          {firstTime
            ? "This is the only form you fill in more than once — everything after it is per vertical. It takes about two minutes."
            : "Coordinators see this alongside every application you send."}
        </p>
      </header>

      {/* Read from the address, not asked for. Shown so it is visible that we
          already know it, rather than quietly assumed. */}
      <Card className="bg-paper-3">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-4">
          <Known label="Name" value={me.user.name} />
          <Known label="BITS ID" value={me.user.bitsId ?? "—"} />
          <Known
            label="Admitted"
            value={me.user.admissionYear ? String(me.user.admissionYear) : "—"}
          />
          <Known label="Email" value={me.user.email} />
        </dl>
        <p className="mt-5 text-[13px] leading-relaxed text-ink-3">
          Taken from your institute Google account. If any of it is wrong, that
          is a question for the ID desk, not for this form.
        </p>
      </Card>

      <form onSubmit={onSubmit} className="flex flex-col gap-8">
        <div className="grid gap-6 sm:grid-cols-2">
          <Field
            label="Phone"
            required
            help="For interview scheduling. Coordinators of verticals you apply to can see it."
          >
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoComplete="tel"
              placeholder="+91 …"
              maxLength={32}
            />
          </Field>

          <Field label="Branch" required help="Duals and minors included.">
            <Input
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              placeholder="B.E. Electronics & Instrumentation"
              maxLength={200}
            />
          </Field>
        </div>

        <Field
          label="About you"
          required
          error={overLong ? `${about.length} of ${ABOUT_MAX} characters.` : undefined}
          help="Two or three sentences: what you build, and what you want to build here."
        >
          <Textarea
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            rows={5}
            placeholder="I write firmware and I have been taking apart cheap ECG boards since first year…"
          />
        </Field>

        {/* The fieldset itself stays a block. A <legend> is laid out against
            its fieldset's border box rather than as a normal child, so in a
            flex container it renders on top of whatever comes first — the
            flex column has to be one level in. */}
        <fieldset>
          <legend className="mono text-ink-3">Links</legend>
          <div className="mt-4 flex flex-col gap-4">
          <p className="text-[13px] text-ink-3">
            Optional, and worth more than the rest of this form put together.
            One GitHub and one LinkedIn; add as many “other” links as you like.
          </p>

          {links.map((link, i) => (
            <div key={i} className="flex gap-3">
              <Select
                aria-label="Link type"
                value={link.kind}
                className="w-[140px] shrink-0"
                options={LINK_KINDS}
                onChange={(kind) =>
                  setLinks((ls) =>
                    ls.map((l, j) => (j === i ? { ...l, kind } : l)),
                  )
                }
              />
              <Input
                aria-label={`${link.kind} URL`}
                value={link.url}
                inputMode="url"
                placeholder={placeholderFor(link.kind)}
                onChange={(e) =>
                  setLinks((ls) =>
                    ls.map((l, j) =>
                      j === i ? { ...l, url: e.target.value } : l,
                    ),
                  )
                }
              />
              <Button
                type="button"
                variant="ghost"
                className="w-11 shrink-0 px-0"
                aria-label="Remove this link"
                onClick={() => setLinks((ls) => ls.filter((_, j) => j !== i))}
              >
                ×
              </Button>
            </div>
          ))}

          {links.length < 8 ? (
            <Button
              type="button"
              variant="ghost"
              className="self-start"
              onClick={() =>
                setLinks((ls) => [...ls, { kind: "other", url: "" }])
              }
            >
              Add a link
            </Button>
          ) : null}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mono text-ink-3">Resume — optional</legend>
          <div className="mt-4 flex flex-col gap-4">
          <ResumeRow resume={profile?.resume ?? null} />
          {fileError ? <Callout tone="error">{fileError}</Callout> : null}
          <div>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              className="sr-only"
              onChange={onPickFile}
            />
            <Button
              type="button"
              variant="ghost"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending
                ? "Uploading"
                : profile?.resume
                  ? "Replace resume"
                  : "Upload resume"}
            </Button>
          </div>
          <p className="text-[13px] leading-relaxed text-ink-3">
            PDF, up to 5MB. Not required — if you have never written one, apply
            anyway and let the links and the paragraph above do the work. You
            can add it later from this page. Only you, the coordinators of
            verticals you apply to, and the admins can open it — it is never on
            a public URL.
          </p>
          </div>
        </fieldset>

        {save.error ? (
          <Callout tone="error">{(save.error as Error).message}</Callout>
        ) : null}

        {firstTime ? (
          <Callout>
            Your profile is saved as you go, and you can come back and change
            any of it. Phone, branch and the paragraph about you are what it
            needs to be finished.
          </Callout>
        ) : null}

        <div className="flex items-center gap-4">
          <Button type="submit" disabled={save.isPending || overLong}>
            {save.isPending ? "Saving" : firstTime ? "Save and continue" : "Save"}
          </Button>
          {save.isSuccess && !save.isPending ? (
            <span className="mono text-ink-3">Saved</span>
          ) : null}
        </div>
      </form>
    </div>
  );
}

function Known({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <dt className="mono text-ink-3">{label}</dt>
      <dd className="truncate text-[15px]" title={value}>
        {value}
      </dd>
    </div>
  );
}
